import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  InputBase,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  Chip,
  IconButton,
  alpha,
  Popper,
  ClickAwayListener,
  Fade,
  useTheme,
  useMediaQuery,
  Divider,
  Grid,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  TrendingUp as TrendingIcon,
  Person as PersonIcon,
  Event as EventIcon,
  Business as BusinessIcon,
  Payment as PaymentIcon,
  Star as StarIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
  PriceChange as PriceChangeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const navigationItems = [
  { id: 'dashboard', type: 'navigation', title: 'Dashboard', subtitle: 'Overview, bookings, analytics, and reports', path: '/mover-portal/dashboard', icon: <NavigateNextIcon /> },
  { id: 'bookings', type: 'navigation', title: 'Bookings', subtitle: 'Review and manage customer jobs', path: '/mover-portal/bookings', icon: <BusinessIcon /> },
  { id: 'calendar', type: 'navigation', title: 'Calendar', subtitle: 'Schedule, availability, and blocked dates', path: '/mover-portal/calendar', icon: <EventIcon /> },
  { id: 'pricing', type: 'navigation', title: 'Pricing', subtitle: 'Rates used by the quote engine', path: '/mover-portal/pricing', icon: <PriceChangeIcon /> },
  { id: 'profile', type: 'navigation', title: 'Profile', subtitle: 'Company details shown to customers', path: '/mover-portal/profile', icon: <PersonIcon /> },
  { id: 'reviews', type: 'navigation', title: 'Reviews', subtitle: 'Customer feedback and ratings', path: '/mover-portal/reviews', icon: <StarIcon /> },
  { id: 'team', type: 'navigation', title: 'Team', subtitle: 'Manage mover team members', path: '/mover-portal/team', icon: <GroupIcon /> },
  { id: 'financials', type: 'navigation', title: 'Earnings & Escrow', subtitle: 'Payouts, escrow, and Stripe setup', path: '/mover-portal/financials', icon: <PaymentIcon /> },
  { id: 'settings', type: 'navigation', title: 'Settings', subtitle: 'Security and portal preferences', path: '/mover-portal/settings', icon: <SettingsIcon /> },
];

const GlobalSearch = ({ token, profile }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [popularActions, setPopularActions] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const anchorRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setPopularActions(navigationItems.slice(0, 6));
  }, [profile]);

  // Load recent searches from localStorage
  useEffect(() => {
    const recent = JSON.parse(localStorage.getItem('mover-recent-searches') || '[]');
    setRecentSearches(recent.slice(0, 5));
  }, []);

  // Search function with debouncing
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query]);

  const performSearch = async (searchQuery) => {
    setLoading(true);
    try {
      const filtered = navigationItems.filter(item =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.subtitle.toLowerCase().includes(searchQuery.toLowerCase())
      );

      setResults(filtered);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleClose = () => {
    setOpen(false);
    setQuery('');
    setResults([]);
  };

  const handleItemClick = (item) => {
    // Save to recent searches
    const recent = JSON.parse(localStorage.getItem('mover-recent-searches') || '[]');
    const newRecent = [item, ...recent.filter(r => r.id !== item.id)].slice(0, 10);
    localStorage.setItem('mover-recent-searches', JSON.stringify(newRecent));
    setRecentSearches(newRecent.slice(0, 5));
    
    // Navigate to item
    navigate(item.path);
    handleClose();
  };

  const handleActionClick = (action) => {
    navigate(action.path);
    handleClose();
  };

  const removeRecentSearch = (itemId, event) => {
    event.stopPropagation();
    const recent = recentSearches.filter(item => item.id !== itemId);
    setRecentSearches(recent);
    localStorage.setItem('mover-recent-searches', JSON.stringify(recent));
  };

  const getItemIcon = (type) => {
    switch (type) {
      case 'booking':
        return <BusinessIcon />;
      case 'customer':
        return <PersonIcon />;
      case 'navigation':
        return <NavigateNextIcon />;
      default:
        return <SearchIcon />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'booking':
        return 'Booking';
      case 'customer':
        return 'Customer';
      case 'navigation':
        return 'Page';
      default:
        return '';
    }
  };

  const renderSearchResults = () => {
    if (loading) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Searching...
          </Typography>
        </Box>
      );
    }

    if (query && results.length === 0) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No results found for "{query}"
          </Typography>
        </Box>
      );
    }

    if (results.length > 0) {
      return (
        <List sx={{ py: 0 }}>
          {results.map((item) => (
            <ListItem
              key={item.id}
              button
              onClick={() => handleItemClick(item)}
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {getItemIcon(item.type)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1">
                      {item.title}
                    </Typography>
                    <Chip
                      label={getTypeLabel(item.type)}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  </Box>
                }
                secondary={item.subtitle}
              />
            </ListItem>
          ))}
        </List>
      );
    }

    return null;
  };

  const renderEmptyState = () => (
    <Box sx={{ p: 3 }}>
      {/* Recent Searches */}
      {recentSearches.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <HistoryIcon sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} />
            <Typography variant="subtitle2" color="text.secondary">
              Recent Searches
            </Typography>
          </Box>
          <List dense sx={{ py: 0 }}>
            {recentSearches.map((item) => (
              <ListItem
                key={item.id}
                button
                onClick={() => handleItemClick(item)}
                sx={{ py: 0.5, borderRadius: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {getItemIcon(item.type)}
                </ListItemIcon>
                <ListItemText
                  primary={item.title}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={(e) => removeRecentSearch(item.id, e)}
                    sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Popular Actions */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <TrendingIcon sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} />
          <Typography variant="subtitle2" color="text.secondary">
            Quick Actions
          </Typography>
        </Box>
        <Grid container spacing={1}>
          {popularActions.map((action) => (
            <Grid item xs={6} key={action.id}>
              <Paper
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'primary.50',
                    transform: 'translateY(-1px)',
                  },
                }}
                onClick={() => handleActionClick(action)}
              >
                <Box sx={{ color: 'primary.main', mb: 0.5 }}>
                  {action.icon}
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  {action.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Search Input */}
      <Box
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          position: 'relative',
          borderRadius: 1,
          backgroundColor: alpha(theme.palette.common.black, 0.04),
          '&:hover': {
            backgroundColor: alpha(theme.palette.common.black, 0.08),
          },
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          width: isMobile ? 40 : 'auto',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            padding: theme.spacing(0, 2),
            height: '100%',
            position: 'absolute',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
          }}
        >
          <SearchIcon />
        </Box>
        
        {!isMobile && (
          <InputBase
            placeholder="Search anything..."
            inputProps={{ 'aria-label': 'search' }}
            sx={{
              color: 'inherit',
              width: '100%',
              '& .MuiInputBase-input': {
                padding: theme.spacing(1, 1, 1, 0),
                paddingLeft: `calc(1em + ${theme.spacing(4)})`,
                width: '200px',
                fontSize: '0.875rem',
              },
            }}
            readOnly
          />
        )}
      </Box>

      {/* Search Dropdown */}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
        style={{ zIndex: 1300, width: isMobile ? '90vw' : '500px' }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <Paper
              elevation={8}
              sx={{
                mt: 1,
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  {/* Search Header */}
                  <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <InputBase
                        ref={inputRef}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search portal pages..."
                        sx={{
                          flex: 1,
                          fontSize: '1rem',
                          '& .MuiInputBase-input': {
                            padding: 0,
                          },
                        }}
                      />
                      <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Search Content */}
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {query ? renderSearchResults() : renderEmptyState()}
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
};

export default GlobalSearch;